import styled from '@emotion/styled';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import React from 'react';
import { Badge } from 'react-bootstrap';

import SafeHtmlOnly from 'App/components/SafeHtmlOnly/SafeHtmlOnly';
import { CheckboxMultiField } from 'Shared/form/CheckboxMultiField';
import { DatePickerField } from 'Shared/form/DatePickerField';
import { NumberField } from 'Shared/form/NumberField';
import { RadioField } from 'Shared/form/RadioField';
import SelectAllField from 'Shared/form/SelectAllField';
import SelectField from 'Shared/form/SelectField';
import { TextareaField } from 'Shared/form/TextareaField';
import { TextField } from 'Shared/form/TextField';
import { patientPropType } from 'Shared/types';
import { rapid3FunctionalScoreQuestionIds, rapid3TotalScoreQuestionIds, viewModes } from '../constants';
import { activityPropType, questionPropType } from '../types';
import getBlockedDays from '../utils/getBlockedDays';
import AssessmentQuestionChildren from './AssessmentQuestionChildren';
import AssessmentQuestionScoring from './AssessmentQuestionScoring';
import AssessmentQuestionText from './AssessmentQuestionText';
import AssessmentRapid3ScoreQuestion from './AssessmentRapid3ScoreQuestion';
import DisplayCMSContent from './DisplayCMSContent';

dayjs.extend(utc);

const MobileAnswerLabelStyle = styled.div`
    padding-bottom: 6px;
`;

const filterDates = R.curry((blockedDaysAttr, date) => {
    const blockedDays = getBlockedDays(blockedDaysAttr);
    return !blockedDays.includes(dayjs.utc(date).day());
});

const getOptionLabel = R.curry((mode, option) => {
    const value = option.option;
    if (mode.label === viewModes.VIEW_MOBILE) {
        return R.prop('mobile_option', option) || value;
    }
    return value;
});

const formatMultiselectOptionLabel = ({ patient, viewMode }) => {
    return (option, { context }) => {
        return (
            <DisplayCMSContent
                content={getOptionLabel(viewMode, option)}
                patient={patient}
                suppressMonographLink={context === 'menu'}
            />
        );
    };
};

const AssessmentQuestionBaseField = ({ question, questionValues, activity, patient, viewMode, onChange }) => {
    const renderChildrenQuestions = (answerId) => {
        return (
            <AssessmentQuestionChildren
                questionValues={questionValues}
                activity={activity}
                patient={patient}
                answerId={answerId}
            />
        );
    };

    const isTotalScoreQuestion = rapid3TotalScoreQuestionIds.includes(question.questionId);
    const isFunctionalScoreQuestion = rapid3FunctionalScoreQuestionIds.includes(question.questionId);
    if (isTotalScoreQuestion || isFunctionalScoreQuestion) {
        return (
            <AssessmentRapid3ScoreQuestion
                activity={activity}
                question={question}
                isFunctionalScore={isFunctionalScoreQuestion}
            />
        );
    }

    const label = (
        <AssessmentQuestionText
            question={question}
            viewMode={viewMode}
            patient={patient}
            showRequiredAsterisk={question.attributes.required}
        />
    );

    const isActivityComplete = activity.status === 1;
    const isActivityDeleted = activity.status === 2;

    const commonProps = {
        fieldName: `${question.questionId}.response`,
        label,
        preInputContent: (
            <>
                {questionValues?.mobileUserResponse && (
                    <MobileAnswerLabelStyle>
                        <Badge variant="success">Mobile answer</Badge>
                    </MobileAnswerLabelStyle>
                )}
                {question.attributes.risk_config && (
                    <AssessmentQuestionScoring question={question} activity={activity} />
                )}
            </>
        ),
        onChange,
        disabled: isActivityComplete || isActivityDeleted,
        required: question.attributes.required,
        suppressAsterisk: true,
    };

    switch (question.type) {
        case 'message':
            return label;
        case 'date': {
            const blockedDays = question.attributes?.blockedDays;
            const filterDatesFn = blockedDays ? filterDates(blockedDays) : undefined;

            return (
                <DatePickerField
                    {...commonProps}
                    debounce
                    filterDate={filterDatesFn}
                    onValidate={(date) => {
                        if (date && filterDatesFn) {
                            const formattedDate = dayjs(date).startOf('day').utc().toISOString();
                            return !filterDatesFn(formattedDate);
                        }
                        return false;
                    }}
                />
            );
        }
        case 'select':
            return (
                <SelectField
                    {...commonProps}
                    options={Object.values(question.answers)}
                    getOptionLabel={getOptionLabel(viewMode)}
                    getOptionValue={R.prop('id')}
                    isClearable
                />
            );
        case 'multiselect':
            return (
                <SelectAllField
                    {...commonProps}
                    options={Object.values(question.answers)}
                    getOptionLabel={getOptionLabel(viewMode)}
                    getOptionValue={R.prop('id')}
                    formatOptionLabel={formatMultiselectOptionLabel({ patient, viewMode })}
                    isClearable
                />
            );
        case 'radio':
            return (
                <RadioField
                    {...commonProps}
                    options={Object.values(question.answers).map((a) => ({
                        ...a,
                        option: <SafeHtmlOnly>{getOptionLabel(viewMode, a)}</SafeHtmlOnly>,
                    }))}
                />
            );
        case 'checkbox': {
            return (
                <CheckboxMultiField
                    {...commonProps}
                    options={Object.values(question.answers).map((a) => ({
                        ...a,
                        option: <DisplayCMSContent content={getOptionLabel(viewMode, a)} patient={patient} />,
                    }))}
                    postOptionsContent={Object.values(question.answers).map((a) => renderChildrenQuestions(a.id))}
                />
            );
        }
        case 'textbox':
            return <TextField {...commonProps} debounce />;
        case 'number':
            return <NumberField {...commonProps} />;
        case 'textarea':
            return <TextareaField {...commonProps} debounce />;
        default:
            return null;
    }
};

AssessmentQuestionBaseField.propTypes = {
    question: questionPropType.isRequired,
    questionValues: questionPropType,
    activity: activityPropType.isRequired,
    patient: patientPropType.isRequired,
    viewMode: PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
    }).isRequired,
    onChange: PropTypes.func.isRequired,
};
AssessmentQuestionBaseField.defaultProps = {
    questionValues: null,
};

export default React.memo(AssessmentQuestionBaseField);
